import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';

function isDiscreetMode(mode) {
  return String(mode || 'PUBLIC').toUpperCase() === 'DISCREET';
}

function ModeBadge({ mode }) {
  if (isDiscreetMode(mode)) {
    return (
      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-semibold">
        DISCREET VERIFICATION
      </span>
    );
  }
  return (
    <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold">
      Public
    </span>
  );
}

function formatChecksSummary(technicalChecks) {
  if (!technicalChecks) return 'Pending';
  if (technicalChecks.filesValid === true && !(technicalChecks.warnings?.length)) {
    return 'Passed';
  }
  const parts = [];
  if (technicalChecks.errors?.length) {
    parts.push(technicalChecks.errors.join('; '));
  }
  if (technicalChecks.warnings?.length) {
    parts.push(`Warnings: ${technicalChecks.warnings.join('; ')}`);
  }
  return parts.length ? parts.join(' | ') : 'Pending';
}

function Verifications() {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [staleOverride, setStaleOverride] = useState(false);
  const [adminRole, setAdminRole] = useState(
    () => localStorage.getItem('adminRole') || 'SUPER_ADMIN'
  );

  useEffect(() => {
    fetchVerifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchVerifications = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPendingVerifications({ page, limit: 20 });
      setVerifications(response.data.verifications);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationDetails = async (userId) => {
    try {
      const response = await adminAPI.getVerificationDetails(userId);
      const data = response.data;
      // Flatten the response structure for easier access
      const signed = data.signedMediaUrls || {};
      setSelectedVerification({
        userId: data.userId,
        email: data.email,
        profileName: data.profileName,
        verificationState: data.verificationState,
        verificationMode: data.verificationMode || data.verificationData?.verificationMode || 'PUBLIC',
        sensitivityLevel: data.sensitivityLevel,
        canViewMedia: data.canViewMedia,
        canPerformActions: data.canPerformActions,
        mediaAccessWarning: data.mediaAccessWarning,
        discreetBadge: data.discreetBadge,
        fraudFlagged: data.fraudFlagged,
        legalHold: data.legalHold,
        currentProfilePictures: Array.isArray(data.currentProfilePictures)
          ? data.currentProfilePictures
          : data.currentPrimaryProfilePicture
            ? [data.currentPrimaryProfilePicture]
            : [],
        currentPrimaryProfilePicture: data.currentPrimaryProfilePicture,
        selectedProfilePicture: signed.selectedProfilePicture
          ? { url: signed.selectedProfilePicture }
          : null,
        selectedProfilePictureIndex: data.verificationData?.selectedProfilePictureIndex,
        verificationPhoto: signed.verificationPhoto
          ? { url: signed.verificationPhoto }
          : null,
        verificationVideo: signed.verificationVideo
          ? { url: signed.verificationVideo }
          : null,
        mediaErrors: data.mediaErrors || {},
        s3Configured: data.s3Configured,
        submittedAt: data.verificationData?.submittedAt,
        verificationType: data.verificationData?.verificationType,
        technicalChecks: data.verificationData?.technicalChecks,
        status: data.verificationData?.status,
        reviewedAt: data.verificationData?.reviewedAt,
        rejectionReason: data.verificationData?.rejectionReason,
        notes: data.verificationData?.notes,
        primaryPictureStaleWarning: data.primaryPictureStaleWarning,
        retryReason: data.retryReason,
      });
      if (data.adminRole) {
        setAdminRole(data.adminRole);
        localStorage.setItem('adminRole', data.adminRole);
      }
      setStaleOverride(false);
      setShowModal(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load verification details');
    }
  };

  const handleApprove = async (
    userId,
    { skipConfirm = false, withStaleOverride = false, approvalMode } = {}
  ) => {
    const modeLabel =
      approvalMode === 'DISCREET'
        ? 'private'
        : approvalMode === 'PUBLIC'
          ? 'public'
          : 'submitted';

    if (
      !skipConfirm &&
      !window.confirm(
        approvalMode
          ? `Approve this verification as ${modeLabel}?`
          : 'Approve this verification using the mode the user submitted?'
      )
    ) {
      return;
    }

    try {
      setProcessing(true);
      const response = await adminAPI.approveVerification(userId, {
        notes,
        adminOverrideStalePrimary: withStaleOverride || staleOverride,
        ...(approvalMode ? { approvalMode } : {}),
      });
      alert(response.data?.message || 'Verification approved successfully!');
      setShowModal(false);
      setNotes('');
      setStaleOverride(false);
      fetchVerifications();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.code === 'STALE_PRIMARY_PHOTO') {
        const override = window.confirm(
          `${err.response.data.error}\n\nApprove anyway with stale primary override?`
        );
        if (override) {
          setStaleOverride(true);
          await handleApprove(userId, { skipConfirm: true, withStaleOverride: true });
          return;
        }
      } else {
        alert(err.response?.data?.error || 'Failed to approve verification');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (userId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    if (!window.confirm('Request retry for this verification? The user will need to submit a new selfie.')) return;

    try {
      setProcessing(true);
      await adminAPI.rejectVerification(userId, { rejectionReason, notes });
      alert('Retry requested successfully.');
      setShowModal(false);
      setRejectionReason('');
      setNotes('');
      fetchVerifications();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject verification');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <h1 className="text-4xl font-bold text-gray-900">Profile Verifications</h1>
        <div className="flex gap-2">
          <Link
            to="/verified-users"
            className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold hover:border-pink-500 transition-colors"
          >
            Verified users
          </Link>
          <button
            type="button"
            onClick={fetchVerifications}
            className="px-4 py-2 bg-pink-500 text-white rounded-xl font-semibold hover:bg-pink-600 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : verifications.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <p className="text-gray-600 text-lg">No pending verifications</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Submitted</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mode</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">State</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Checks</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {verifications.map((verification) => (
                  <tr key={verification.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      {verification.profileName || 'No name'}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{verification.email}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {formatDate(verification.submittedAt)}
                    </td>
                    <td className="px-6 py-4">
                      <ModeBadge mode={verification.verificationMode} />
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                        {verification.verificationState || 'PENDING_REVIEW'}
                      </span>
                      {!isDiscreetMode(verification.verificationMode) &&
                        verification.primaryChangedSinceSubmit && (
                        <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                          Stale primary
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-600 max-w-xs">
                      {formatChecksSummary(verification.technicalChecks)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => fetchVerificationDetails(verification.userId)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-pink-500 transition-colors"
            >
              Previous
            </button>
            <span className="text-gray-700 font-semibold">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-pink-500 transition-colors"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Verification Details Modal */}
      {showModal && selectedVerification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Verification Review</h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedVerification(null);
                    setRejectionReason('');
                    setNotes('');
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                {selectedVerification.profileName || 'No name'} ({selectedVerification.email})
              </p>
            </div>

            <div className="p-6">
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl text-red-900 text-sm">
                <p className="font-bold text-base">SENSITIVE PRIVATE DATA</p>
                <p className="mt-1">Access is logged. Signed URLs expire shortly.</p>
                {adminRole === 'SUPPORT_ADMIN' && (
                  <p className="mt-2 font-semibold">
                    Your role cannot view verification photos or videos.
                  </p>
                )}
              </div>

              {selectedVerification.verificationState && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                    {selectedVerification.verificationState}
                  </span>
                  <ModeBadge mode={selectedVerification.verificationMode} />
                </div>
              )}

              {selectedVerification.fraudFlagged && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-300 rounded-lg text-orange-900 text-sm font-semibold">
                  Fraud flag active — extended retention / legal hold may apply
                </div>
              )}

              {isDiscreetMode(selectedVerification.verificationMode) && (
                <div className="mb-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl text-purple-900 text-sm">
                  <p className="font-semibold text-base mb-2">DISCREET VERIFICATION</p>
                  <p>Closeted / discreet submission — no public profile photo match required.</p>
                  <p className="mt-2 text-purple-800">
                    Verification media is admin-only and must never appear on public profile APIs.
                  </p>
                </div>
              )}

              {!isDiscreetMode(selectedVerification.verificationMode) &&
                selectedVerification.primaryPictureStaleWarning && (
                <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl text-amber-900 text-sm">
                  <p className="font-semibold mb-2">Primary photo changed since submission</p>
                  <p>{selectedVerification.primaryPictureStaleWarning.message}</p>
                  <p className="mt-2 break-all text-xs">
                    Submission: {selectedVerification.primaryPictureStaleWarning.primaryPictureUrlAtSubmission}
                  </p>
                  <p className="mt-1 break-all text-xs">
                    Current: {selectedVerification.primaryPictureStaleWarning.currentPrimaryPictureUrl}
                  </p>
                </div>
              )}

              <div className="mb-6 p-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl text-indigo-900 text-sm">
                <p className="font-semibold mb-2">Human liveness review (manual)</p>
                {!selectedVerification.verificationVideo?.url &&
                  selectedVerification.technicalChecks?.videoExists !== true &&
                  !selectedVerification.mediaErrors?.verificationVideo && (
                  <p className="mb-2 text-amber-800 font-medium">No verification video submitted yet.</p>
                )}
                {!selectedVerification.verificationVideo?.url &&
                  (selectedVerification.mediaErrors?.verificationVideo ||
                    selectedVerification.technicalChecks?.videoExists === true) && (
                  <p className="mb-2 text-red-800 font-medium">Verification video was submitted but could not be loaded.</p>
                )}
                <p className="mb-2">When video is present, confirm:</p>
                <ul className="list-disc list-inside space-y-1 text-indigo-800">
                  <li>User blinks once</li>
                  <li>Turns head left</li>
                  <li>Turns head right</li>
                </ul>
                <p className="mt-2 text-xs text-indigo-700">
                  No automated pose detection — your judgment only. Recommended clip length: 3–5 seconds.
                </p>
              </div>

              {selectedVerification.technicalChecks && (
                <div className="mb-6 p-4 bg-gray-50 rounded-xl text-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Technical checks</h3>
                  <p>
                    <strong>Mode:</strong>{' '}
                    {isDiscreetMode(selectedVerification.technicalChecks.verificationMode ||
                      selectedVerification.verificationMode)
                      ? 'Private / discreet'
                      : 'Public'}
                  </p>
                  <p className="mt-1">
                    <strong>Files valid:</strong>{' '}
                    {selectedVerification.technicalChecks.filesValid ? 'Yes' : 'No'}
                  </p>
                  {selectedVerification.technicalChecks.videoDurationSec != null && (
                    <p className="mt-1">
                      <strong>Video duration:</strong>{' '}
                      {selectedVerification.technicalChecks.videoDurationSec}s
                    </p>
                  )}
                  {selectedVerification.technicalChecks.errors?.length > 0 && (
                    <p className="mt-2 text-red-700">
                      <strong>Errors:</strong>{' '}
                      {selectedVerification.technicalChecks.errors.join('; ')}
                    </p>
                  )}
                  {selectedVerification.technicalChecks.warnings?.length > 0 && (
                    <p className="mt-2 text-amber-800">
                      <strong>Warnings:</strong>{' '}
                      {selectedVerification.technicalChecks.warnings.join('; ')}
                    </p>
                  )}
                </div>
              )}

              {/* All profile photos on the account (current) */}
              {selectedVerification.currentProfilePictures?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Profile photos (current on account)
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {selectedVerification.currentProfilePictures.length} photo
                    {selectedVerification.currentProfilePictures.length === 1 ? '' : 's'} — index 0 is primary.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {selectedVerification.currentProfilePictures.map((url, index) => {
                      const isPrimary = index === 0;
                      const isSubmissionPhoto =
                        !isDiscreetMode(selectedVerification.verificationMode) &&
                        selectedVerification.selectedProfilePictureIndex != null &&
                        index === selectedVerification.selectedProfilePictureIndex;
                      return (
                        <div key={`${url}-${index}`} className="flex flex-col items-center gap-1">
                          <img
                            src={url}
                            alt={`Profile ${index + 1}`}
                            className={`w-28 h-28 sm:w-32 sm:h-32 object-cover rounded-xl border-2 ${
                              isSubmissionPhoto
                                ? 'border-pink-500 ring-2 ring-pink-200'
                                : isPrimary
                                  ? 'border-blue-400'
                                  : 'border-gray-200'
                            }`}
                          />
                          <span className="text-xs text-gray-600">
                            #{index}
                            {isPrimary ? ' · primary' : ''}
                            {isSubmissionPhoto ? ' · matched at submit' : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Photo matched at submission — public verification only */}
              {!isDiscreetMode(selectedVerification.verificationMode) &&
                selectedVerification.selectedProfilePicture?.url && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Profile photo at verification (submission)
                  </h3>
                  <div className="flex gap-4 flex-wrap">
                    <img
                      src={selectedVerification.selectedProfilePicture.url}
                      alt="Profile at submission"
                      className="w-48 h-48 object-cover rounded-xl border-2 border-pink-300"
                    />
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Index at submit:</strong>{' '}
                        {selectedVerification.selectedProfilePictureIndex ?? '—'}
                      </p>
                      {selectedVerification.selectedProfilePicture.isApproved !== undefined && (
                        <p className="text-sm text-gray-600">
                          <strong>Status:</strong>{' '}
                          <span className={selectedVerification.selectedProfilePicture.isApproved ? 'text-green-600' : 'text-red-600'}>
                            {selectedVerification.selectedProfilePicture.isApproved ? 'Approved' : 'Rejected'}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {selectedVerification.canViewMedia === false && (
                <div className="mb-6 p-4 bg-gray-100 rounded-xl text-gray-700 text-sm">
                  Metadata-only access for your admin role. Media is hidden.
                </div>
              )}

              {selectedVerification.canViewMedia !== false &&
                (selectedVerification.s3Configured === false ||
                  Object.keys(selectedVerification.mediaErrors || {}).length > 0) && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl text-red-900 text-sm">
                  <p className="font-semibold">Verification media could not be opened</p>
                  {selectedVerification.s3Configured === false && (
                    <p className="mt-1">The API server has no configured S3 credentials.</p>
                  )}
                  {Object.entries(selectedVerification.mediaErrors || {}).map(([media, message]) => (
                    <p key={media} className="mt-1 break-words">
                      <strong>{media}:</strong> {message}
                    </p>
                  ))}
                </div>
              )}

              {/* Verification Photo */}
              {selectedVerification.canViewMedia !== false &&
                selectedVerification.verificationPhoto && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isDiscreetMode(selectedVerification.verificationMode)
                      ? 'Private Verification Selfie'
                      : 'Verification Photo'}
                  </h3>
                  <div className="flex gap-4">
                    <img
                      src={selectedVerification.verificationPhoto.url}
                      alt="Verification Photo"
                      className="w-48 h-48 object-cover rounded-xl border-2 border-gray-200"
                    />
                    <div className="flex-1">
                      {selectedVerification.verificationPhoto.isApproved !== undefined && (
                        <p className="text-sm text-gray-600">
                          <strong>Status:</strong>{' '}
                          <span className={selectedVerification.verificationPhoto.isApproved ? 'text-green-600' : 'text-red-600'}>
                            {selectedVerification.verificationPhoto.isApproved ? 'Approved' : 'Rejected'}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Verification Video */}
              {selectedVerification.canViewMedia !== false &&
                selectedVerification.verificationVideo?.url && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Verification Video</h3>
                  <div className="flex gap-4">
                    <video
                      src={selectedVerification.verificationVideo.url}
                      controls
                      className="w-full max-w-md rounded-xl border-2 border-gray-200"
                    />
                    <div className="flex-1">
                      {selectedVerification.verificationVideo.isApproved !== undefined && (
                        <p className="text-sm text-gray-600">
                          <strong>Status:</strong>{' '}
                          <span className={selectedVerification.verificationVideo.isApproved ? 'text-green-600' : 'text-red-600'}>
                            {selectedVerification.verificationVideo.isApproved ? 'Approved' : 'Rejected'}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Submission Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Submission Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <strong className="text-gray-700">Submitted:</strong>{' '}
                    <span className="text-gray-600">{formatDate(selectedVerification.submittedAt)}</span>
                  </div>
                  <div>
                    <strong className="text-gray-700">Mode:</strong>{' '}
                    <span className="text-gray-600">
                      {isDiscreetMode(selectedVerification.verificationMode)
                        ? 'Private / discreet'
                        : 'Public'}
                    </span>
                  </div>
                  <div>
                    <strong className="text-gray-700">Type:</strong>{' '}
                    <span className="text-gray-600">{selectedVerification.verificationType || 'manual'}</span>
                  </div>
                </div>
              </div>

              {/* Notes Input */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this verification..."
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                  rows="3"
                />
              </div>

              {/* Retry reason (shown to user) */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Retry reason (required to request retry)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain what the user should fix and resubmit..."
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
                  rows="3"
                />
              </div>

              {!isDiscreetMode(selectedVerification.verificationMode) && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900">
                  <p className="font-semibold mb-1">Admin mode override</p>
                  <p>
                    User submitted <strong>public</strong> verification. If you trust the
                    verification selfie/video but not their profile photos, use{' '}
                    <strong>Approve as private</strong> — they stay verified but won&apos;t get a
                    public photo-matched badge while profile photos remain.
                  </p>
                </div>
              )}

              {isDiscreetMode(selectedVerification.verificationMode) &&
                selectedVerification.currentProfilePictures?.length > 0 && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900">
                  <p className="font-semibold mb-1">Admin mode override</p>
                  <p>
                    User submitted <strong>private</strong> verification but has profile photos.
                    Use <strong>Approve as public</strong> if photos match the verification media.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {selectedVerification.canPerformActions !== false && (
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => handleApprove(selectedVerification.userId)}
                  disabled={processing}
                  className="flex-1 min-w-[140px] px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing
                    ? 'Processing...'
                    : `Approve (${isDiscreetMode(selectedVerification.verificationMode) ? 'private' : 'public'})`}
                </button>
                {!isDiscreetMode(selectedVerification.verificationMode) && (
                  <button
                    onClick={() =>
                      handleApprove(selectedVerification.userId, { approvalMode: 'DISCREET' })
                    }
                    disabled={processing}
                    className="flex-1 min-w-[140px] px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? 'Processing...' : 'Approve as private'}
                  </button>
                )}
                {isDiscreetMode(selectedVerification.verificationMode) && (
                  <button
                    onClick={() =>
                      handleApprove(selectedVerification.userId, { approvalMode: 'PUBLIC' })
                    }
                    disabled={
                      processing || !selectedVerification.currentProfilePictures?.length
                    }
                    title={
                      !selectedVerification.currentProfilePictures?.length
                        ? 'Public verification requires profile photos'
                        : undefined
                    }
                    className="flex-1 min-w-[140px] px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? 'Processing...' : 'Approve as public'}
                  </button>
                )}
                <button
                  onClick={() => handleReject(selectedVerification.userId)}
                  disabled={processing}
                  className="flex-1 min-w-[140px] px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Processing...' : 'Request retry'}
                </button>
                <button
                  type="button"
                  disabled={processing}
                  onClick={async () => {
                    const reason = window.prompt('Fraud flag reason (optional):');
                    if (reason === null) return;
                    try {
                      setProcessing(true);
                      await adminAPI.setVerificationFraudFlag(selectedVerification.userId, {
                        fraudFlagged: true,
                        legalHold: true,
                        reason: reason || undefined,
                      });
                      alert('Fraud flag and legal hold applied.');
                    } catch (err) {
                      alert(err.response?.data?.error || 'Failed to set fraud flag');
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50"
                >
                  Flag fraud
                </button>
              </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Verifications;
